/**
 * Generic JSX building blocks for tool message formatters.
 *
 * Tools' `message: { call, result, error, denied }` callbacks return
 * JSX rendered through the instructions reconciler. These components are
 * the canonical scaffolding — composing them keeps the wire shape
 * consistent across tools without string-concatenation helpers. Dataset
 * rendering lives in `<DatasetPreview>`.
 *
 * - `<DefaultToolCall>` — default `call` slot rendering.
 * - `<DefaultToolOutcome>` — default settled-outcome rendering,
 *   dispatching on the outcome kind.
 */

import { encode } from '@toon-format/toon';
import { createElement, type ReactNode } from 'react';

import type { ToolOutputError } from '#plugin-chat/lib/tool/output-types.ts';
import type { SettledOutcome } from '#plugin-chat/lib/tool/settled-outcome.ts';

interface DefaultToolCallProps {
  args: Record<string, unknown>;
}

/**
 * Default `message.call` rendering — used when a tool doesn't declare
 * its own. Emits one `<arg-*>` tag per argument inside the enclosing
 * `<tool-call>` record envelope: plain scalars inline, structured
 * values TOON-encoded.
 */
export function DefaultToolCall({ args }: DefaultToolCallProps) {
  const argElements = Object.entries(args).map(([key, value]) => {
    const isPlain =
      typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null;
    // The tag name is data-driven, so the element is built via
    // createElement — JSX requires a statically-typed element type.
    return createElement(`tag-arg-${key}`, { key }, isPlain ? String(value) : encode(value));
  });
  if (argElements.length === 0) {
    return null;
  }
  return <>{argElements}</>;
}

interface ToolErrorMessageProps {
  toolName: string;
  envelope: ToolOutputError;
  /**
   * Optional tool-specific recovery guidance, rendered between the
   * standard "failed (errorType): message" line and the structured
   * suggestion list. Lets a tool add loop-friendly "fix X and retry"
   * prose without re-implementing the surrounding scaffold.
   */
  recovery?: ReactNode;
}

/**
 * Shared scaffold for failure transcript prose: the canonical
 * "failed (errorType): message" line, an optional tool-specific
 * `recovery` slot, and the structured `suggestions` list. Single source
 * of truth for failure rendering — {@link DefaultToolOutcome} and every
 * per-tool `message.error` formatter compose this rather than repeating
 * the markup.
 *
 * Always the full failure rendering. A *non-last* failed call is
 * compacted by the transcript's result slot (via `useCompact`), which
 * bypasses this component entirely — there is no reachable compact
 * branch here to keep.
 */
export function ToolErrorMessage({ toolName, envelope, recovery }: ToolErrorMessageProps) {
  const suggestions = envelope.suggestions ?? [];
  return (
    <>
      <p>
        The <code>{toolName}</code> tool call failed ({envelope.errorType}): {envelope.error}
      </p>
      {recovery}
      {suggestions.length > 0 && (
        <>
          <p>Suggestions:</p>
          <ul>
            {suggestions.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

interface DefaultToolOutcomeProps {
  outcome: SettledOutcome;
  toolName: string;
}

/**
 * Default settled-outcome rendering — used when a tool doesn't declare
 * a formatter for the outcome's kind, and as the degradation target
 * when a declared formatter throws. Errors cover both tool-level
 * failures and protocol errors, which arrive through the same
 * {@link ToolOutputError} envelope; denials surface the human
 * approval-refusal reason so the LLM sees why the call did not run.
 */
export function DefaultToolOutcome({ outcome, toolName }: DefaultToolOutcomeProps) {
  if (outcome.kind === 'denied') {
    return (
      <p>
        The <code>{toolName}</code> tool call was denied by the user: {outcome.reason}
      </p>
    );
  }
  if (outcome.kind === 'error') {
    return <ToolErrorMessage envelope={outcome.envelope} toolName={toolName} />;
  }
  if (outcome.kind === 'result') {
    return (
      <p>
        Tool <code>{toolName}</code> completed.
      </p>
    );
  }
  return outcome satisfies never;
}
